import {
  ArrowBendDownRightIcon,
  CheckCircleIcon,
  CopyIcon,
  GitCommitIcon,
  LightbulbIcon,
  ProhibitIcon,
  TerminalWindowIcon,
  WarningOctagonIcon,
} from '@phosphor-icons/react';
import type {
  AnalysisReport,
  AnalysisWarning,
  MatchedRule,
  PathAnalysis,
  WarningCode,
} from '../types';
import { useI18n, type TranslationParams } from '../i18n';

interface WhyPanelProps {
  readonly report: AnalysisReport | null;
  readonly selectedPath: string | null;
  readonly onApplySuggestion: (warning: AnalysisWarning) => void;
  readonly onCopyCommand: (command: string) => void;
}

function selectedAnalysis(report: AnalysisReport | null, path: string | null): PathAnalysis | null {
  if (!report || !path) return null;
  return report.paths.find((entry) => entry.path === path) ?? null;
}

const WARNING_MESSAGE_KEYS: Readonly<Record<WarningCode, string>> = {
  'blocked-negation': 'warningMessages.blockedNegation',
  'broad-rule': 'warningMessages.broadRule',
  'sensitive-path-unignored': 'warningMessages.sensitiveUnignored',
  'sensitive-path-reincluded': 'warningMessages.sensitiveReincluded',
};

const WARNING_REASON_KEYS: Readonly<Record<WarningCode, string>> = {
  'blocked-negation': 'warningReasons.blockedNegation',
  'broad-rule': 'warningReasons.broadRule',
  'sensitive-path-unignored': 'warningReasons.sensitiveUnignored',
  'sensitive-path-reincluded': 'warningReasons.sensitiveReincluded',
};

interface LocalizedText {
  readonly key: string;
  readonly params?: TranslationParams;
}

function pathExplanation(analysis: PathAnalysis): LocalizedText {
  const blocked = analysis.matchedRules.find((match) => match.blocked);
  if (blocked !== undefined) {
    return { key: 'explanations.path.blocked', params: { rule: blocked.raw } };
  }
  if (analysis.decisiveRule === null) {
    return {
      key: analysis.ignored ? 'explanations.path.ignoredByAncestor' : 'explanations.path.noMatch',
    };
  }
  return analysis.ignored
    ? {
        key: 'explanations.path.ignoredByRule',
        params: {
          rule: analysis.decisiveRule.raw,
          source: analysis.decisiveRule.source,
          line: analysis.decisiveRule.line,
        },
      }
    : {
        key: 'explanations.path.reincludedByRule',
        params: {
          rule: analysis.decisiveRule.raw,
          source: analysis.decisiveRule.source,
          line: analysis.decisiveRule.line,
        },
      };
}

function matchExplanation(match: MatchedRule): LocalizedText {
  if (match.blocked) {
    return { key: 'explanations.match.blocked', params: { rule: match.raw } };
  }
  if (match.negated) {
    return {
      key:
        match.stateAfter === 'tracked'
          ? 'explanations.match.reincluded'
          : 'explanations.match.negationNoChange',
      params: { rule: match.raw },
    };
  }
  return {
    key:
      match.stateAfter === 'ignored'
        ? 'explanations.match.ignored'
        : 'explanations.match.overridden',
    params: { rule: match.raw },
  };
}

function warningParams(warning: AnalysisWarning): TranslationParams {
  return {
    path: warning.paths[0] ?? '',
    count: warning.paths.length,
    source: warning.source ?? '.gitignore',
    line: warning.line ?? 0,
  };
}

export function WhyPanel({
  report,
  selectedPath,
  onApplySuggestion,
  onCopyCommand,
}: WhyPanelProps) {
  const { t } = useI18n();
  const analysis = selectedAnalysis(report, selectedPath);
  const localizedPathExplanation = analysis === null ? null : pathExplanation(analysis);

  return (
    <section id="panel-why" className="panel why-panel" aria-labelledby="why-heading">
      <div className="panel-heading">
        <div>
          <p className="panel-kicker">{t('panels.why.kicker')}</p>
          <h2 id="why-heading">{t('panels.why.title')}</h2>
        </div>
      </div>

      {!analysis ? (
        <div className="empty-state why-empty">
          <GitCommitIcon aria-hidden="true" />
          <h3>{t('panels.why.empty')}</h3>
          <p>{t('panels.why.emptyHelp')}</p>
        </div>
      ) : (
        <div className="why-content">
          <div className="path-title-block">
            <code>{analysis.path}</code>
            <span className={`result-badge ${analysis.ignored ? 'is-ignored' : 'is-tracked'}`}>
              {analysis.ignored ? (
                <ProhibitIcon aria-hidden="true" />
              ) : (
                <CheckCircleIcon aria-hidden="true" />
              )}
              {analysis.ignored ? t('states.ignored') : t('states.tracked')}
            </span>
          </div>

          <div className="decision-block">
            <span className="section-label">{t('panels.why.decision')}</span>
            {analysis.decisiveRule ? (
              <>
                <code className="decision-rule">{analysis.decisiveRule.raw}</code>
                <dl className="decision-meta">
                  <div>
                    <dt>{t('panels.why.source')}</dt>
                    <dd>
                      <code>{analysis.ruleSource}</code>
                    </dd>
                  </div>
                  <div>
                    <dt>{t('panels.why.line')}</dt>
                    <dd>{analysis.ruleLine}</dd>
                  </div>
                </dl>
              </>
            ) : (
              <p>{t('panels.why.noMatchingRule')}</p>
            )}
            <p className="human-explanation">
              {localizedPathExplanation === null
                ? null
                : t(localizedPathExplanation.key, localizedPathExplanation.params)}
            </p>
          </div>

          <div className="timeline-section">
            <div className="subsection-heading">
              <h3>{t('panels.why.timeline')}</h3>
              <span>{t('panels.why.matchCount', { count: analysis.matchedRules.length })}</span>
            </div>
            <ol className="match-timeline">
              {analysis.matchedRules.map((match) => {
                const localizedMatchExplanation = matchExplanation(match);
                return (
                  <li
                    key={`${match.id}-${String(match.line)}`}
                    className={`${match.decisive ? 'is-decisive' : ''}${match.blocked ? ' is-blocked' : ''}`}
                  >
                    <span className="timeline-marker" aria-hidden="true">
                      <ArrowBendDownRightIcon />
                    </span>
                    <div className="timeline-card">
                      <div className="timeline-rule">
                        <code>{match.raw}</code>
                        {match.decisive ? <span>{t('panels.why.decisive')}</span> : null}
                        {match.blocked ? <span>{t('panels.why.blocked')}</span> : null}
                      </div>
                      <p>{t(localizedMatchExplanation.key, localizedMatchExplanation.params)}</p>
                      <div className="timeline-meta">
                        <code>
                          {match.source}:{String(match.line)}
                        </code>
                        <span>
                          {t('panels.why.stateAfter')}:{' '}
                          {match.stateAfter === 'ignored'
                            ? t('states.ignored')
                            : t('states.tracked')}
                        </span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
            {analysis.matchedRules.length === 0 ? (
              <p className="empty-inline">{t('panels.why.noTimeline')}</p>
            ) : null}
          </div>

          {analysis.warnings.length > 0 ? (
            <div className="warning-section">
              <h3>
                <WarningOctagonIcon aria-hidden="true" /> {t('panels.why.warnings')}
              </h3>
              {analysis.warnings.map((warning) => (
                <article className="warning-item" key={warning.id}>
                  <strong>{t(WARNING_MESSAGE_KEYS[warning.code], warningParams(warning))}</strong>
                  <p>{t(WARNING_REASON_KEYS[warning.code], warningParams(warning))}</p>
                  {warning.suggestedRule ? (
                    <div className="suggestion-row">
                      <code>{warning.suggestedRule}</code>
                      <button
                        className="button button-small"
                        type="button"
                        onClick={() => {
                          onApplySuggestion(warning);
                        }}
                      >
                        <LightbulbIcon aria-hidden="true" /> {t('actions.applySuggestion')}
                      </button>
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          ) : null}

          <div className="commands-section">
            <h3>
              <TerminalWindowIcon aria-hidden="true" /> {t('panels.why.commands')}
            </h3>
            {[`git check-ignore -v -- ${analysis.path}`, `git rm --cached -- ${analysis.path}`].map(
              (command) => (
                <div className="command-row" key={command}>
                  <code>{command}</code>
                  <button
                    className="icon-button icon-button-small"
                    type="button"
                    aria-label={t('actions.copyCommand')}
                    onClick={() => {
                      onCopyCommand(command);
                    }}
                  >
                    <CopyIcon aria-hidden="true" />
                  </button>
                </div>
              ),
            )}
            <small>{t('panels.why.trackedNote')}</small>
          </div>
        </div>
      )}
    </section>
  );
}
